import React from "react";
import {
    Row,
    Col,
    Table,
    Tag,
    Progress,
} from "antd";
import { Column } from "@ant-design/plots";
import { useSelector } from "react-redux";

// Constants
import { TEMPLATE } from '../../../../constants'

// Components 
import { 
    SectionCard,
    StatCard 
} from "./components";

// Services Library
import { REDUX as REDUX_UTIL } from "../../../../services/util";

// Styles
import styles from './index.module.css'

const { reduxStoreWrapper } = REDUX_UTIL

const {
    monthComparisonColumns,
    calculateGradeDistribution,
    improvementStatus,
    fleetRiskIndex,
    colorClassification,
    conclusionStatus,
    driverScoreGenerator,
    COLORS, GRADE_COLORS
} = TEMPLATE.DRIVER_DASHBOARD

// ---------- Main component ----------
const DriverPerformanceDashboard = () => {
    
    const KPIDashboards = useSelector(reduxStoreWrapper('drivers.KPIDashboards'))

    const {
        driverLogsByCreatedAt = [], 
        driverSumCurMonth = {}, 
        driverSumPrevMonth = {},
        topEvents = []
    } = KPIDashboards

    const data = {
        stats: [
            {
                key: "fleetScore",
                label: "OVERALL FLEET SCORE",
                value: driverSumCurMonth.overallFleetScore,
                badge: { text: `Grade ${driverScoreGenerator(driverSumCurMonth.overallFleetScore)}`, color: colorClassification(driverSumCurMonth.overallFleetScore, 100, true), bg: COLORS.amberBg },
                footnote: `${driverSumCurMonth.overallFleetScore - driverSumPrevMonth.overallFleetScore} points vs last month`,
                footnoteColor: COLORS.red,
            },
            {
                key: "driversDE",
                label: "DRIVERS D / E",
                value: driverSumCurMonth.totalBadDriver,
                badge: improvementStatus(driverSumPrevMonth.totalBadDriver, driverSumCurMonth.totalBadDriver),
                footnoteColor: COLORS.textMuted,
            },
            {
                key: "safetyEvents",
                label: "SAFETY EVENTS",
                value: driverLogsByCreatedAt.length,
                // badge: { text: `${}%`, color: 'error' },
                footnote: topEvents.length && topEvents?.map(e => e.eventType)?.join(', ') || '',
                footnoteColor: COLORS.textMuted,
            },
            {
                key: "complianceIssues",
                label: "COMPLIANCE ISSUES",
                value: driverSumCurMonth.overallComplianceScore,
                badge: improvementStatus(driverSumPrevMonth.overallComplianceScore, driverSumCurMonth.overallComplianceScore),
                // footnote: "Geofence, driving hours, checklist",
                footnoteColor: COLORS.textMuted,
            },
            {
                key: "fuelAlerts",
                label: "FUEL ALERTS",
                value: "-",
                // badge: { text: "Review", color: 'warning' },
                // footnote: "Below benchmark or high idling",
                footnoteColor: COLORS.textMuted,
            },
            {
                key: "riskIndex",
                label: "FLEET RISK INDEX",
                bigText: fleetRiskIndex(driverSumCurMonth.overallFleetScore),
                // footnote: "14 drivers require review",
                // footnoteColor: COLORS.textMuted,
            },
        ],
        monthComparison: [
            {
                metric: "Overall Fleet Score",
                previous: driverSumPrevMonth.overallFleetScore,
                current: driverSumCurMonth.overallFleetScore,
                change: driverSumCurMonth.overallFleetScore - driverSumCurMonth.overallFleetScore,
                status: conclusionStatus(driverSumCurMonth.overallFleetScore - driverSumPrevMonth.overallFleetScore),
            },
            {
                metric: "Safety Avg /55",
                previous: driverSumPrevMonth.overallSafetyScore,
                current: driverSumCurMonth.overallSafetyScore,
                change: driverSumCurMonth.overallSafetyScore - driverSumPrevMonth.overallSafetyScore,
                status: conclusionStatus(driverSumCurMonth.overallSafetyScore - driverSumPrevMonth.overallSafetyScore),
            },
            {
                metric: "Compliance Avg /35",
                previous: driverSumPrevMonth.overallComplianceScore,
                current: driverSumCurMonth.overallComplianceScore,
                change: driverSumCurMonth.overallComplianceScore - driverSumCurMonth.overallComplianceScore,
                status: conclusionStatus(driverSumCurMonth.overallComplianceScore - driverSumCurMonth.overallComplianceScore),
            },
            {
                metric: "Fuel Efficiency Avg /10",
                previous: driverSumPrevMonth.overallidlingAndfuelEfficiencyScore,
                current: driverSumCurMonth.overallidlingAndfuelEfficiencyScore,
                change: driverSumCurMonth.overallidlingAndfuelEfficiencyScore - driverSumPrevMonth.overallidlingAndfuelEfficiencyScore,
                status: conclusionStatus(driverSumCurMonth.overallidlingAndfuelEfficiencyScore - driverSumPrevMonth.overallidlingAndfuelEfficiencyScore),
            },
            {
                metric: "Drivers D/E",
                previous: driverSumPrevMonth.totalBadDriver,
                current: driverSumCurMonth.totalBadDriver,
                change: driverSumCurMonth.totalBadDriver - driverSumPrevMonth.totalBadDriver,
                status: conclusionStatus(driverSumCurMonth.totalBadDriver - driverSumPrevMonth.totalBadDriver),
            },
        ],
        scoreBreakdown: [
        {
            key: "safety",
            label: "Safety",
            value: driverSumCurMonth.overallSafetyScore?.toFixed() || 0,
            max: 55,
            color: colorClassification((driverSumCurMonth.overallSafetyScore?.toFixed() || 0), 55),
            desc: "Lower mainly due to speeding, fatigue, seatbelt warning, and harsh braking events.",
        },
        {
        key: "compliance",
        label: "Compliance",
        value: driverSumCurMonth.overallComplianceScore?.toFixed() || 0,
        max: 35,
        color: colorClassification((driverSumCurMonth.overallComplianceScore?.toFixed() || 0), 35),
        desc: "Impacted by missing pre-trip checklist, geofence violations, and unauthorized stops.",
        },
        {
        key: "fuel",
        label: "Fuel Efficiency",
        value: driverSumCurMonth.overallidlingAndfuelEfficiencyScore?.toFixed() || 0,
        max: 10,
        color: colorClassification((driverSumCurMonth.overallidlingAndfuelEfficiencyScore?.toFixed() || 0), 10),
        desc: "Fuel score uses benchmark efficiency plus idling score. 9 vehicles are still below target.",
        },
        ],
        topIssues: topEvents.sort((a, b) => b.count - a.count)?.map((event, i) => ({
            rank: i + 1,
            title: event.eventType,
            desc: `${event.count} Cases`,
            severity: "-",
        })),
        gradeDistribution: calculateGradeDistribution(driverLogsByCreatedAt),
        formulaChips: [
            "Safety Point = 55 - Safety Penalty",
            "Compliance Point = 35 - Compliance Penalty",
            "Fuel Point = Efficiency /7 + Idling /3",
        ],
    }
    
    return (
        <div className={styles["container"]}>

            {/* Stat cards */}
            <Row gutter={16}>
                    {
                        data.stats.map(({ key, ...s }) => (
                            <Col span={4} key={key}>
                                <StatCard key={key} {...s} />
                            </Col>
                        ))
                    }
            </Row>

            {/* Score breakdown + grade distribution */}
            <Row gutter={20} style={{ marginTop: 20 }}>
                <Col span={14}>
                    <SectionCard
                        title="Enhanced Score Breakdown"
                        subtitle="Fleet average based on active drivers in selected timeframe"
                        extra={<Tag>Driver Score = Safety + Compliance + Fuel</Tag>}
                    >
                        <Row gutter={16}>
                            {data.scoreBreakdown.map((s) => (
                                <Col span={8} key={s.key}>
                                    <div className={styles["scoreBreakdownContainer"]} >
                                        <div className={styles["scoreBreakdownTitle"]}>{s.label}</div>
                                        <div className={styles["scoreBreakdownLabel"]}>
                                            {s.value} <span className={styles["scoreBreakdownMax"]}>/ {s.max}</span>
                                        </div>
                                        <Progress
                                            percent={(s.value / s.max) * 100}
                                            showInfo={false}
                                            strokeColor={s.color}
                                            trailColor="#eef0f6"
                                            strokeWidth={7}
                                        />
                                        <div className={styles["scoreBreakdownDesc"]}>{s.desc}</div>
                                    </div>
                                </Col>
                            ))}
                        </Row>

                        <Row gutter={12} style={{ marginTop: 16 }}>
                            {data.formulaChips.map((chip) => (
                                <Col key={chip}>
                                    <div
                                        style={{
                                            border: `1px solid ${COLORS.border}`,
                                            borderRadius: 10,
                                            padding: "8px 14px",
                                            fontSize: 12.5,
                                            fontWeight: 600,
                                            color: "#374151",
                                            background: "#f9fafc",
                                        }}
                                    >
                                        {chip}
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </SectionCard>
                </Col>

                <Col span={10}>
                    <SectionCard
                        title="Driver Grade Distribution"
                        subtitle="A >85, B 75–85, C 65–75, D 55–65, E <55"
                    >
                        <Column
                            data={data.gradeDistribution}
                            xField="grade"
                            yField="count"
                            height={280}
                            columnWidthRatio={0.5}
                            label={{
                                position: "top",
                                formatter: (datum) => datum.count || '',
                                style: { fontWeight: 700, fill: "#111827" },
                            }}
                            colorField="grade" 
                            color={(datum) => GRADE_COLORS[datum.grade]}
                            xAxis={{ label: { style: { fontWeight: 600 } } }}
                            yAxis={false}
                        />
                    </SectionCard>
                </Col>
            </Row>

            {/* Month comparison + top issues */}
            <Row gutter={20} style={{ marginTop: 20 }}>
                <Col span={12}>
                    <SectionCard
                        title="Current Month vs Previous Month"
                        subtitle="Higher score is better. Lower issue count is better."
                    >
                        <Table
                            columns={monthComparisonColumns}
                            dataSource={data.monthComparison}
                            rowKey="metric"
                            pagination={false}
                            size="small"
                        />
                    </SectionCard>
                </Col>

                <Col span={12}>
                    <SectionCard
                        title="Top Issues This Month"
                        subtitle="Ranked by severity, case count, and increase vs last month"
                    >
                        <div className={styles["topIssuesContainer"]}>
                            {data.topIssues.map((issue) => (
                                <div key={issue.rank} style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                                    <div
                                        style={{
                                        width: 26,
                                        height: 26,
                                        borderRadius: 8,
                                        background: COLORS.grayBg,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontWeight: 700,
                                        fontSize: 12.5,
                                        flexShrink: 0,
                                        }}
                                    >
                                        {issue.rank}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 700, fontSize: 13.5 }}>{issue.title}</div>
                                        <div style={{ color: COLORS.textMuted, fontSize: 12.5, marginTop: 2 }}>
                                        {issue.desc}
                                        </div>
                                    </div>
                                    <Tag color={issue.severity === "High" ? 'error': 'warning'}>{issue.severity}</Tag>
                                </div>
                            ))}
                        </div>
                    </SectionCard>
                </Col>
            </Row>

        </div>
);
}

export default React.memo(DriverPerformanceDashboard)